import React, { useState, useEffect, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import BaseModal from '../BaseModal';
import Button from '../Button';
import { useOmpDealerContext } from '../../../components/Vdp/OmpDealerProvider';
import getSelectedDealerId from '../../helpers/getSelectedDealerId';
import DealerSelectDropdown from '../../../components/Vdp/DealerSelectDropdown';

import isOmpPlatform from '../../utils/isOmpPlatform';
import theme from '../../themes/tradeRev/theme';

import { TradeViewVariant } from '../../types/trade';

import {
  StyledTitleContainer,
  StyledTitle,
  StyledActions,
  StyledPrimaryButton,
  StyledIcon,
  ConfirmationMsg
} from './Styles';

const propTypes = {
  isOpen: PropTypes.bool.isRequired,
  width: PropTypes.number,
  isLoading: PropTypes.bool,
  icon: PropTypes.string,
  modalLogic: PropTypes.arrayOf(
    PropTypes.shape({
      msg: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.object,
        PropTypes.func
      ]),
      title: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
      confirmLabel: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
      onConfirm: PropTypes.func,
      cancelLabel: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
      onCancel: PropTypes.func,
      onClickAway: PropTypes.func,
      showDealershipSelect: PropTypes.bool
    })
  ).isRequired,
  loadingMsg: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  canContinue: PropTypes.bool
};

const defaultProps = {
  width: 490,
  icon: null,
  isLoading: false,
  loadingMsg: 'Loading ...',
  canContinue: true
};

const ConfirmModal = ({
  isOpen,
  width,
  isLoading,
  icon,
  modalLogic,
  loadingMsg,
  canContinue,
  location,
  setBiddingDealerId
}) => {
  const [title, setTitle] = useState('');
  const [msg, setMsg] = useState('');
  const [confirmationLabel, setConfirmationLabel] = useState('');
  const [cancellationLabel, setCancellationLabel] = useState('');
  const [runConfirm, setRunConfirm] = useState(() => {});
  const [runCancel, setRunCancel] = useState(() => {});
  const [runClickAway, setRunClickAway] = useState(() => {});
  const [currentModalPos, setCurrentModalPos] = useState(0);
  const [showDealershipSelect, setShowDealershipSelect] = useState(false);
  const isOmp = isOmpPlatform();

  const {
    isMultiOrgAndEnabled,
    ompBiddingDealer,
    validOmpDealerships,
    isOmpMultiOrgDealer
  } = useOmpDealerContext();

  const dealerId = useMemo(
    () =>
      getSelectedDealerId(
        validOmpDealerships,
        ompBiddingDealer?.organizationId
      ),
    [ompBiddingDealer?.organizationId, validOmpDealerships]
  );

  useEffect(() => {
    if (
      isMultiOrgAndEnabled &&
      isOmpMultiOrgDealer &&
      setBiddingDealerId &&
      showDealershipSelect
    ) {
      setBiddingDealerId(dealerId);
    }
  }, [
    dealerId,
    isMultiOrgAndEnabled,
    isOmpMultiOrgDealer,
    location,
    setBiddingDealerId,
    showDealershipSelect
  ]);

  const setModalDisplayed = useCallback(() => {
    setTitle(modalLogic[currentModalPos].title);
    setMsg(modalLogic[currentModalPos].msg);
    setConfirmationLabel(modalLogic[currentModalPos].confirmLabel);
    setCancellationLabel(modalLogic[currentModalPos].cancelLabel);
    setRunConfirm(modalLogic[currentModalPos].onConfirm);
    setRunCancel(modalLogic[currentModalPos].onCancel);
    setRunClickAway(modalLogic[currentModalPos].onClickAway);
    setShowDealershipSelect(
      !!modalLogic[currentModalPos]?.showDealershipSelect
    );
  }, [currentModalPos, modalLogic]);

  const onCancel = e => {
    e.preventDefault();
    e.stopPropagation();

    if (runCancel) runCancel();
  };

  const onClickAway = e => {
    e.preventDefault();
    e.stopPropagation();

    if (runClickAway) runClickAway();
  };

  useEffect(() => {
    if (!currentModalPos || currentModalPos + 1 > modalLogic?.length) {
      setCurrentModalPos(0);
    }
  }, [currentModalPos, modalLogic?.length]);

  useEffect(() => {
    if (modalLogic?.length && modalLogic?.length > currentModalPos) {
      setModalDisplayed();
    }
  }, [currentModalPos, modalLogic?.length, setModalDisplayed]);

  const onConfirm = e => {
    e.preventDefault();
    e.stopPropagation();

    if (runConfirm) runConfirm();

    if (canContinue && modalLogic?.length > currentModalPos + 1) {
      setCurrentModalPos(position => position + 1);
    }
  };

  return (
    <BaseModal
      testId="confirm-modal"
      width={width}
      isOpen={isOpen}
      padding="36px"
      isClickOverlayClose
      onRequestClose={onClickAway}
      hasParentSelector={isOmp && location === TradeViewVariant.VDP}
    >
      <StyledTitleContainer>
        {icon && <StyledIcon icon={icon} color={theme.COLORS.TERTIARY} />}
        <StyledTitle>{title}</StyledTitle>
      </StyledTitleContainer>
      <ConfirmationMsg data-testid="confirmation-msg">{msg}</ConfirmationMsg>
      {showDealershipSelect && (
        <DealerSelectDropdown
          hasError={!ompBiddingDealer && isMultiOrgAndEnabled}
        />
      )}
      <StyledActions>
        {runCancel && (
          <Button.Transparent
            data-testid="confirmation-cancel"
            onClick={onCancel}
            disabled={isLoading}
          >
            {cancellationLabel || defaultProps.cancelLabel}
          </Button.Transparent>
        )}
        <StyledPrimaryButton
          data-testid="confirmation-yes"
          onClick={onConfirm}
          disabled={isLoading}
        >
          {isLoading ? loadingMsg : confirmationLabel}
        </StyledPrimaryButton>
      </StyledActions>
    </BaseModal>
  );
};

ConfirmModal.propTypes = propTypes;
ConfirmModal.defaultProps = defaultProps;

export default ConfirmModal;
